import { useState } from 'react'
import { Head, Link, usePage } from '@inertiajs/react'
import { usePublicPage } from '@/hooks/usePublicPage'
import { LandingNavbar } from '@/components/landing/LandingNavbar'
import { LandingFooter } from '@/components/landing/LandingFooter'
import { Button } from '@/components/ui/button'
import {
  Clock, CheckCircle2, ChevronDown, ChevronUp, ArrowRight,
  Layers, ShieldCheck, Check, Zap, Eye, HelpCircle, Award, BookOpen,
  Search, MessageSquare
} from 'lucide-react'
import type { PageProps } from '@/types'

interface SampleSolution {
  first_principles?: string
  speed_shortcut?: string
  case_check?: string
  visual_logic?: string
}

interface SampleQuestion {
  question_text: string
  options?: string[]
  correct_answer?: string
  question_type: string
  marks: number
  negative_marks: number
  solutions?: SampleSolution
}

interface ExamPatternSection {
  name: string
  questions_count: number
  marks: number
  description: string
}

interface ExamPattern {
  sections: ExamPatternSection[]
  marking_rules: string[]
}

interface FAQItem {
  q: string
  a: string
}

interface ExamData {
  slug: string
  title: string
  badge: string
  name: string
  category: string
  tagline: string
  overview: string
  stats: { label: string; value: string }[]
  pattern: ExamPattern
  branches?: { name: string; code: string; topics: string[] }[]
  sample_questions?: SampleQuestion[]
  published_tests?: { id: number; title: string; duration_minutes: number; questions_count: number }[]
  faqs?: FAQItem[]
  seo: {
    title: string
    description: string
    keywords: string
  }
}

export default function PublicExamsShow({ exam }: { exam: ExamData }) {
  const { auth } = usePage<PageProps>().props
  usePublicPage()
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0)
  const [activeSolutionTab, setActiveSolutionTab] = useState<Record<number, keyof SampleSolution>>({})
  const [faqSearch, setFaqSearch] = useState('')

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index)
  }

  const setSolutionTab = (qIndex: number, tab: keyof SampleSolution) => {
    setActiveSolutionTab(prev => ({ ...prev, [qIndex]: tab }))
  }

  // Generate JSON-LD Schema
  const jsonLdFaq = exam.faqs && exam.faqs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: exam.faqs.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.a
      }
    }))
  } : null

  const jsonLdCourse = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: exam.title,
    description: exam.seo.description,
    provider: {
      '@type': 'EducationalOrganization',
      name: 'PrepKiya',
      url: 'https://prepkiya.com',
      logo: 'https://prepkiya.com/landing-logo.png'
    },
    educationalLevel: 'Competitive Examination Preparation',
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'online',
      courseWorkload: 'PT180M'
    }
  }

  const jsonLdBreadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://prepkiya.com'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Exams',
        item: 'https://prepkiya.com/exams'
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: exam.name,
        item: `https://prepkiya.com/exams/${exam.slug}`
      }
    ]
  }

  return (
    <>
      <Head title={exam.seo.title}>
        <meta name="description" content={exam.seo.description} />
        <meta name="keywords" content={exam.seo.keywords} />
        <link rel="canonical" href={`https://prepkiya.com/exams/${exam.slug}`} />
        <meta property="og:title" content={exam.seo.title} />
        <meta property="og:description" content={exam.seo.description} />
        <meta property="og:url" content={`https://prepkiya.com/exams/${exam.slug}`} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdCourse) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdBreadcrumb) }}
        />
        {jsonLdFaq && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdFaq) }}
          />
        )}
      </Head>

      <div className="min-h-screen w-full flex flex-col bg-white text-slate-900 antialiased selection:bg-brand-gold/20 selection:text-slate-900">
        <LandingNavbar user={auth?.user} />

        <main className="w-full flex-1 flex flex-col">
          {/* Hero Section matching LandingHero UX */}
          <section className="w-full pt-6 pb-10 sm:pt-14 sm:pb-20 bg-white border-b border-slate-100">
            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              
              {/* Breadcrumb Trail */}
              <nav className="flex items-center gap-1.5 sm:gap-2 text-[11px] sm:text-xs font-semibold text-slate-500 mb-4 sm:mb-6 overflow-x-auto whitespace-nowrap pb-1">
                <Link href="/" className="hover:text-[#131b2e] transition-colors shrink-0">Home</Link>
                <span>/</span>
                <Link href="/exams" className="hover:text-[#131b2e] transition-colors shrink-0">Exams</Link>
                <span>/</span>
                <span className="text-[#131b2e] truncate">{exam.name}</span>
              </nav>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 items-start">
                <div className="lg:col-span-7 space-y-4 sm:space-y-6 text-left">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="!font-sans inline-flex items-center px-2.5 sm:px-3 py-1 rounded-full text-[11px] sm:text-xs font-bold bg-[#f4f7fb] text-[#131b2e] border border-slate-200 shadow-2xs">
                      {exam.category}
                    </span>
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-gold/15 text-brand-gold text-[11px] sm:text-xs font-black border border-brand-gold/30 shadow-2xs">
                      <Award className="w-3.5 h-3.5 shrink-0" />
                      <span>{exam.badge}</span>
                    </div>
                  </div>

                  <h1 className="!font-sans text-2xl sm:text-4xl lg:text-[46px] font-black text-[#131b2e] tracking-tight leading-tight">
                    {exam.title}
                  </h1>

                  <p className="!font-sans text-sm sm:text-lg text-slate-600 leading-relaxed font-normal">
                    {exam.tagline}
                  </p>

                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 pt-1 sm:pt-2">
                    <Button
                      asChild
                      className="!font-sans w-full sm:w-auto bg-[#c8933b] hover:bg-[#b5802a] text-white font-bold h-11 sm:h-12 px-6 sm:px-7 rounded-xl shadow-md text-sm sm:text-base transition-all group cursor-pointer"
                    >
                      <a href="/users/sign_up" className="flex items-center justify-center gap-2">
                        <span>Start Free Mock Test</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </a>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      className="!font-sans w-full sm:w-auto bg-transparent hover:bg-slate-100 border-2 border-[#131b2e] text-[#131b2e] font-bold px-5 sm:px-6 h-11 sm:h-12 rounded-xl text-sm sm:text-base transition-all cursor-pointer"
                    >
                      <a href="#pattern">
                        Exam Pattern & Syllabus
                      </a>
                    </Button>
                  </div>
                </div>

                {/* Right Column: Blueprint Snapshot Card */}
                <div className="lg:col-span-5 w-full flex justify-center lg:justify-end">
                  <div className="w-full max-w-[420px] bg-white border-2 border-[#131b2e]/10 rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all">
                    {/* Dark Navy Header */}
                    <div className="bg-[#131b2e] text-white px-5 sm:px-6 py-3.5 sm:py-4 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full bg-[#c8933b] animate-pulse shrink-0" />
                        <h3 className="!font-sans font-bold text-sm sm:text-base text-white tracking-wide">
                          Exam Blueprint Snapshot
                        </h3>
                      </div>
                      <span className="!font-sans text-[10px] sm:text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white/10 text-slate-200 border border-white/10">
                        Official Pattern
                      </span>
                    </div>

                    {/* Card Body */}
                    <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                      <div className="grid grid-cols-2 gap-2 sm:gap-3">
                        {exam.stats.map((stat, i) => {
                          const isOddLast = exam.stats.length % 2 !== 0 && i === exam.stats.length - 1
                          return (
                            <div
                              key={i}
                              className={`p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl bg-[#f8fafc] border border-slate-200 flex flex-col justify-between ${
                                isOddLast ? 'col-span-2' : ''
                              }`}
                            >
                              <span className="!font-sans text-[10px] sm:text-[11px] font-semibold text-slate-500 block">
                                {stat.label}
                              </span>
                              <span className="!font-sans text-xs sm:text-sm font-black text-[#131b2e] mt-1 block leading-snug">
                                {stat.value}
                              </span>
                            </div>
                          )
                        })}
                      </div>

                      <div className="pt-1 text-[11px] sm:text-xs text-slate-500 flex items-center gap-2">
                        <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#c8933b] shrink-0" />
                        <span>Calibrated against the latest official exam notification</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </section>

          {/* Overview & Specializations */}
          <section className="w-full py-10 sm:py-20 bg-[#f8fafc] border-b border-slate-200/80">
            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6 sm:space-y-8">
              <div className="max-w-4xl space-y-3 sm:space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 text-[#131b2e] text-[11px] sm:text-xs font-bold tracking-wide uppercase shadow-2xs">
                  <BookOpen className="w-3.5 h-3.5 text-[#c8933b]" />
                  <span>Comprehensive Exam Guide</span>
                </div>
                <h2 className="!font-sans text-xl sm:text-3xl lg:text-4xl font-black text-[#131b2e] tracking-tight">
                  About {exam.name}
                </h2>
                <p className="!font-sans text-sm sm:text-base text-slate-600 leading-relaxed font-normal">
                  {exam.overview}
                </p>
              </div>

              {/* Branches / Streams Grid */}
              {exam.branches && exam.branches.length > 0 && (
                <div className="pt-2 sm:pt-4 space-y-4 sm:space-y-5">
                  <h3 className="!font-sans text-lg sm:text-xl font-bold text-[#131b2e]">
                    Covered Disciplines & Stream Papers
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-5">
                    {exam.branches.map((b) => (
                      <div key={b.code} className="p-4 sm:p-5 bg-white border border-slate-200 rounded-2xl shadow-xs hover:border-brand-gold/60 transition-all space-y-2.5">
                        <div className="flex items-center justify-between">
                          <span className="!font-sans text-xs font-black px-2.5 py-1 rounded-md bg-brand-gold/15 text-brand-gold border border-brand-gold/30">
                            {b.code}
                          </span>
                          <span className="!font-sans text-[10px] text-slate-400 font-semibold">Official Paper</span>
                        </div>
                        <h4 className="!font-sans text-sm font-bold text-[#131b2e] leading-snug">{b.name}</h4>
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {b.topics.map((t, ti) => (
                            <span key={ti} className="!font-sans text-[10px] font-medium px-2 py-0.5 rounded-md bg-[#f4f7fb] text-slate-600 border border-slate-200">
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Pattern & Marking Table */}
          <section id="pattern" className="w-full py-16 sm:py-24 bg-white border-b border-slate-100">
            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
              <div className="space-y-3 max-w-3xl">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#f4f7fb] border border-slate-200 text-[#131b2e] text-xs font-bold tracking-wide uppercase">
                  <Clock className="w-3.5 h-3.5 text-[#c8933b]" />
                  <span>Sectional Weightage</span>
                </div>
                <h2 className="!font-sans text-3xl sm:text-4xl font-black text-[#131b2e] tracking-tight">
                  Official Exam Pattern & Marking Scheme
                </h2>
                <p className="!font-sans text-base text-slate-600">
                  Detailed distribution of questions, allocated marks, and accuracy deductions.
                </p>
              </div>

              {exam.pattern.sections.length > 0 && (
                <>
                  {/* Mobile Card Layout (< 768px) */}
                  <div className="block md:hidden space-y-3.5">
                    {exam.pattern.sections.map((sec, i) => (
                      <div
                        key={i}
                        className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 space-y-3 shadow-2xs"
                      >
                        {/* Header: Section Name & Badges */}
                        <div className="space-y-2">
                          <h3 className="!font-sans font-bold text-base text-[#131b2e] leading-snug">
                            {sec.name}
                          </h3>
                          <div className="flex items-center gap-2">
                            <span className="!font-sans text-xs font-bold px-2.5 py-1 rounded-lg bg-[#f4f7fb] text-[#131b2e] border border-slate-200">
                              {sec.questions_count} Questions
                            </span>
                            <span className="!font-sans text-xs font-bold px-2.5 py-1 rounded-lg bg-amber-500/10 text-amber-900 border border-amber-500/20">
                              {sec.marks} Marks
                            </span>
                          </div>
                        </div>

                        {/* Description / Topics */}
                        <div className="pt-2 border-t border-slate-100">
                          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                            Syllabus Scope & Topics
                          </span>
                          <p className="!font-sans text-xs text-slate-600 leading-relaxed">
                            {sec.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop / Tablet Table Layout (>= 768px) */}
                  <div className="hidden md:block overflow-x-auto border border-slate-200 rounded-3xl bg-white shadow-xs">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-[#f8fafc] text-xs uppercase font-bold text-slate-700 border-b border-slate-200">
                        <tr>
                          <th className="px-6 py-4">Section Name</th>
                          <th className="px-6 py-4">Questions</th>
                          <th className="px-6 py-4">Marks</th>
                          <th className="px-6 py-4">Syllabus Scope & Topics</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {exam.pattern.sections.map((sec, i) => (
                          <tr key={i} className="hover:bg-slate-50/80 transition-colors">
                            <td className="px-6 py-4 font-bold text-[#131b2e] whitespace-nowrap">{sec.name}</td>
                            <td className="px-6 py-4 text-slate-600 font-semibold">{sec.questions_count} Qs</td>
                            <td className="px-6 py-4 text-[#131b2e] font-bold">{sec.marks} Marks</td>
                            <td className="px-6 py-4 text-xs sm:text-sm text-slate-600 leading-relaxed">{sec.description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* Marking Rules Card */}
              <div className="p-4 sm:p-8 rounded-2xl sm:rounded-3xl bg-[#f8fafc] border border-slate-200 space-y-3 sm:space-y-4">
                <h4 className="!font-sans text-xs font-bold uppercase tracking-wider text-[#131b2e]">
                  Marking Rules & Accuracy Penalties:
                </h4>
                <ul className="space-y-2.5 sm:space-y-3">
                  {exam.pattern.marking_rules.map((rule, ri) => (
                    <li key={ri} className="!font-sans flex items-start gap-2.5 sm:gap-3 text-xs sm:text-sm text-slate-700 font-medium">
                      <div className="mt-0.5 w-4 h-4 rounded-full bg-white border border-slate-200 text-[#131b2e] flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-[#131b2e] stroke-[3]" />
                      </div>
                      <span className="leading-relaxed">{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* 4-Method Solutions Section matching PremiumFeaturesSection dark navy theme */}
          {exam.sample_questions && exam.sample_questions.length > 0 && (
            <section className="w-full py-12 sm:py-24 bg-[#131b2e] text-white">
              <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8 sm:space-y-12">
                <div className="text-center max-w-3xl mx-auto space-y-3 sm:space-y-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-brand-gold border border-white/10 text-[11px] sm:text-xs font-bold tracking-wider uppercase">
                    <Layers className="w-3.5 h-3.5 text-brand-gold" />
                    <span>Exclusive 4-Method Solutions</span>
                  </div>
                  <h2 className="!font-sans text-2xl sm:text-4xl lg:text-[44px] font-black text-white tracking-tight leading-tight">
                    Solve Questions through 4 Distinct Analytical Lenses
                  </h2>
                  <p className="!font-sans text-xs sm:text-base text-slate-300 max-w-2xl mx-auto leading-relaxed">
                    Textbook solutions take 5+ minutes. PrepKiya trains you in speed shortcuts, boundary sanity checks, and visual spatial intuition for the exam hall.
                  </p>
                </div>

                <div className="space-y-6 sm:space-y-8">
                  {exam.sample_questions.map((sq, qi) => {
                    const currentTab = activeSolutionTab[qi] || 'first_principles'
                    return (
                      <div key={qi} className="bg-white text-slate-900 rounded-2xl sm:rounded-3xl p-4 sm:p-8 shadow-2xl border border-slate-100 space-y-4 sm:space-y-6">
                        <div className="flex flex-wrap items-center justify-between gap-2.5 border-b border-slate-100 pb-3 sm:pb-4">
                          <div className="flex items-center gap-2">
                            <span className="!font-sans text-xs font-black px-2.5 py-1 rounded-lg bg-[#131b2e] text-white">
                              Question {qi + 1}
                            </span>
                            <span className="!font-sans text-xs font-bold px-2 py-1 rounded-md bg-[#f4f7fb] text-[#131b2e] border border-slate-200">
                              {sq.question_type}
                            </span>
                          </div>
                          <span className="!font-sans text-[11px] sm:text-xs font-semibold text-slate-500">
                            Reward: +{sq.marks} | Penalty: -{sq.negative_marks}
                          </span>
                        </div>

                        <p className="!font-sans text-sm sm:text-lg font-bold text-[#131b2e] leading-relaxed">
                          {sq.question_text}
                        </p>

                        {/* Options */}
                        {sq.options && sq.options.length > 0 && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 pt-1">
                            {sq.options.map((opt, oi) => {
                              const isCorrect = opt === sq.correct_answer
                              return (
                                <div
                                  key={oi}
                                  className={`p-3 sm:p-3.5 rounded-xl sm:rounded-2xl border text-xs sm:text-sm font-medium flex items-center justify-between ${
                                    isCorrect
                                      ? 'border-emerald-500/60 bg-emerald-50 text-emerald-900 font-bold'
                                      : 'border-slate-200 bg-[#f8fafc] text-slate-700'
                                  }`}
                                >
                                  <span>{opt}</span>
                                  {isCorrect && <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 ml-2" />}
                                </div>
                              )
                            })}
                          </div>
                        )}

                        {sq.question_type === 'NAT' && (
                          <div className="p-3 sm:p-3.5 rounded-xl sm:rounded-2xl border border-emerald-300 bg-emerald-50 text-emerald-900 text-xs sm:text-sm font-bold inline-block">
                            Correct Numerical Entry: {sq.correct_answer}
                          </div>
                        )}

                        {/* 4-Method Solution Tabs */}
                        {sq.solutions && (
                          <div className="pt-3 sm:pt-4 border-t border-slate-100 space-y-3 sm:space-y-4">
                            <span className="!font-sans text-xs font-bold uppercase tracking-wider text-slate-500 block">
                              Select Solution Method:
                            </span>

                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-2.5">
                              {[
                                { key: 'first_principles' as const, label: 'First Principles', time: '~60s', icon: Layers },
                                { key: 'speed_shortcut' as const, label: 'Speed Shortcut', time: '<5s', icon: Zap },
                                { key: 'case_check' as const, label: 'Boundary Check', time: '~20s', icon: ShieldCheck },
                                { key: 'visual_logic' as const, label: 'Visual Logic', time: '~15s', icon: Eye }
                              ].map(tab => {
                                const TabIcon = tab.icon
                                const isActive = currentTab === tab.key
                                return (
                                  <button
                                    key={tab.key}
                                    type="button"
                                    onClick={() => setSolutionTab(qi, tab.key)}
                                    className={`p-2.5 sm:p-3 rounded-xl sm:rounded-2xl border text-left transition-all cursor-pointer flex flex-col justify-between ${
                                      isActive
                                        ? 'bg-[#131b2e] text-white border-[#131b2e] shadow-md'
                                        : 'bg-[#f8fafc] border-slate-200 text-slate-700 hover:border-slate-300'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between mb-1">
                                      <TabIcon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isActive ? 'text-brand-gold' : 'text-slate-500'}`} />
                                      <span className={`text-[9px] sm:text-[10px] font-bold px-1.5 sm:px-2 py-0.5 rounded-full ${isActive ? 'bg-white/15 text-slate-200' : 'bg-slate-200/70 text-slate-600'}`}>
                                        {tab.time}
                                      </span>
                                    </div>
                                    <span className="!font-sans text-[11px] sm:text-xs font-bold block truncate">{tab.label}</span>
                                  </button>
                                )
                              })}
                            </div>

                            <div className="p-4 sm:p-5 rounded-xl sm:rounded-2xl bg-[#f8fafc] border border-slate-200 text-slate-800 text-xs sm:text-sm leading-relaxed font-normal">
                              {sq.solutions[currentTab] || 'Step-by-step breakdown available in the interactive mock test attempt.'}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            </section>
          )}

          {/* Full Mock Tests Cards */}
          {exam.published_tests && exam.published_tests.length > 0 && (
            <section className="w-full py-12 sm:py-24 bg-[#f8fafc] border-b border-slate-200/80">
              <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6 sm:space-y-8">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-1">
                    <h2 className="!font-sans text-xl sm:text-3xl font-black text-[#131b2e]">
                      Available Mock Tests for {exam.name}
                    </h2>
                    <p className="!font-sans text-xs sm:text-sm text-slate-600">
                      Full-length practice test papers with sectional locks and instant percentile ranking.
                    </p>
                  </div>
                  <Button asChild variant="outline" className="!font-sans font-bold border-slate-300 hover:bg-white text-[#131b2e] rounded-xl h-9 sm:h-10 px-4 sm:px-5 text-xs self-start sm:self-auto">
                    <Link href="/mock-tests">View All Test Series</Link>
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {exam.published_tests.map((test) => (
                    <div key={test.id} className="bg-white border border-slate-200 rounded-2xl sm:rounded-3xl p-5 sm:p-7 flex flex-col justify-between shadow-xs hover:shadow-md transition-all space-y-4 sm:space-y-5">
                      <div className="space-y-2.5">
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span className="flex items-center gap-1.5 font-semibold text-slate-600">
                            <Clock className="w-4 h-4 text-[#c8933b]" />
                            {test.duration_minutes} Minutes
                          </span>
                          <span className="!font-sans text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
                            Full Practice Test
                          </span>
                        </div>
                        <h4 className="!font-sans font-bold text-base sm:text-lg text-[#131b2e] line-clamp-2">{test.title}</h4>
                      </div>

                      <Button asChild className="!font-sans w-full bg-[#131b2e] hover:bg-[#1e293b] text-white font-bold h-10 sm:h-11 rounded-xl shadow-sm text-xs cursor-pointer transition-all">
                        <a href="/users/sign_up">
                          Attempt Mock Test
                        </a>
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* FAQs section matching clean PrepKiya design tokens */}
          {exam.faqs && exam.faqs.length > 0 && (
            <section className="w-full py-12 sm:py-24 bg-white border-b border-slate-100">
              <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6 sm:space-y-10">
                <div className="text-center space-y-3 sm:space-y-4 max-w-2xl mx-auto">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f4f7fb] border border-slate-200 text-[#131b2e] text-[11px] sm:text-xs font-bold uppercase tracking-wider">
                    <HelpCircle className="w-3.5 h-3.5 text-[#c8933b]" />
                    <span>Frequently Asked Questions</span>
                  </div>
                  <h2 className="!font-sans text-2xl sm:text-4xl font-black text-[#131b2e] tracking-tight leading-tight">
                    Frequently Asked Questions
                  </h2>
                  <p className="!font-sans text-xs sm:text-base text-slate-600">
                    Official insights, marking rules, test format, and effective preparation guidelines for {exam.name}.
                  </p>
                </div>

                {/* FAQ Quick Search */}
                <div className="relative max-w-lg mx-auto w-full">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    value={faqSearch}
                    onChange={(e) => setFaqSearch(e.target.value)}
                    placeholder="Search FAQs (e.g. calculator, negative marking)..."
                    className="!font-sans w-full pl-10 pr-4 py-2.5 sm:py-3 text-xs sm:text-sm bg-[#f8fafc] border border-slate-200 rounded-xl sm:rounded-2xl text-[#131b2e] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#131b2e]/20 focus:border-[#131b2e] transition-all"
                  />
                  {faqSearch && (
                    <button
                      type="button"
                      onClick={() => setFaqSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 hover:text-slate-700 px-1.5 py-0.5"
                    >
                      Clear
                    </button>
                  )}
                </div>

                {/* FAQ Accordion List */}
                <div className="space-y-3">
                  {exam.faqs
                    .filter(faq =>
                      !faqSearch.trim() ||
                      faq.q.toLowerCase().includes(faqSearch.toLowerCase()) ||
                      faq.a.toLowerCase().includes(faqSearch.toLowerCase())
                    )
                    .map((faq, fi) => {
                      const isOpen = openFaqIndex === fi
                      return (
                        <div
                          key={fi}
                          className={`border rounded-xl sm:rounded-2xl transition-all duration-200 overflow-hidden ${
                            isOpen
                              ? 'bg-white border-[#131b2e]/30 shadow-md ring-1 ring-[#131b2e]/5'
                              : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50/50'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => toggleFaq(fi)}
                            className="w-full px-4 sm:px-6 py-3.5 sm:py-5 text-left flex items-center justify-between gap-3 font-bold text-xs sm:text-base text-[#131b2e] cursor-pointer"
                          >
                            <div className="flex items-start gap-2.5 sm:gap-3.5 pr-1">
                              <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#f4f7fb] border border-slate-200 text-[#131b2e] text-[10px] sm:text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                                Q{fi + 1}
                              </span>
                              <span className="leading-snug">{faq.q}</span>
                            </div>
                            <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                              isOpen ? 'bg-[#131b2e] text-white' : 'bg-slate-100 text-slate-500'
                            }`}>
                              {isOpen ? <ChevronUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#c8933b]" /> : <ChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                            </div>
                          </button>
                          {isOpen && (
                            <div className="px-4 sm:px-6 pb-4 sm:pb-6 pt-1 text-xs sm:text-[15px] text-slate-600 leading-relaxed border-t border-slate-100 sm:ml-9">
                              {faq.a}
                            </div>
                          )}
                        </div>
                      )
                    })}
                </div>

                {/* Footer Help Note */}
                <div className="p-3.5 sm:p-4 rounded-xl sm:rounded-2xl bg-[#f8fafc] border border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-2.5 text-xs text-slate-600 text-center sm:text-left">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-[#c8933b] shrink-0" />
                    <span>Have another question not answered above?</span>
                  </div>
                  <Link
                    href="/help"
                    className="font-bold text-[#131b2e] hover:underline flex items-center gap-1"
                  >
                    Visit Help Center <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </section>
          )}

          {/* Bottom Conversion Section */}
          <section className="w-full py-12 sm:py-24 bg-[#f8fafc]">
            <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4 sm:space-y-6">
              <h2 className="!font-sans text-2xl sm:text-4xl lg:text-[44px] font-black text-[#131b2e] tracking-tight leading-tight">
                Ready to Boost Your {exam.name} Rank?
              </h2>
              <p className="!font-sans text-xs sm:text-lg text-slate-600 max-w-xl mx-auto leading-relaxed">
                Join thousands of serious aspirants. Practice with timed computer-based tests, learn with 4-method solutions, and identify your weak topics immediately.
              </p>
              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4">
                <Button asChild size="lg" className="!font-sans w-full sm:w-auto bg-[#131b2e] hover:bg-[#1e293b] text-white font-bold h-11 sm:h-12 px-6 sm:px-8 rounded-xl shadow-md text-sm sm:text-base cursor-pointer">
                  <a href="/users/sign_up">
                    Start Your First Test Free
                  </a>
                </Button>
                <Button asChild variant="outline" size="lg" className="!font-sans w-full sm:w-auto border-2 border-slate-300 hover:bg-white text-[#131b2e] font-bold h-11 sm:h-12 px-6 sm:px-7 rounded-xl text-sm sm:text-base cursor-pointer">
                  <Link href="/exams">
                    Explore Other Exams
                  </Link>
                </Button>
              </div>
            </div>
          </section>
        </main>

        <LandingFooter />
      </div>
    </>
  )
}
